/**
 * LES DESSINS DU MUSEE: le modele, ses bornes, et sa relecture.
 *
 * Module PUR: ni DOM, ni canvas, ni horloge. C'est ce qui permet au serveur de
 * valider EXACTEMENT ce que le navigateur a compose, avec le meme code — et
 * c'est indispensable ici plus qu'ailleurs: un dessin est la seule chose du jeu
 * qu'un joueur fabrique librement et que tous les autres verront.
 *
 * CE QU'UN DESSIN EST. Une liste de TRAITS, chacun une couleur, une epaisseur
 * et une suite de points. Les coordonnees sont des ENTIERS sur une grille de
 * 0 a `COTE_DESSIN`, jamais des pixels: un dessin fait au doigt sur un
 * telephone de 390 points de large doit se rouvrir tel quel sur un ecran de
 * 1080, et se relire dans dix ans sans qu'on ait a savoir sur quoi il a ete
 * trace.
 *
 * POURQUOI DES BORNES PARTOUT. Le contenu vient d'un joueur, il traverse le
 * reseau, il est stocke, et il sera redessine chez les autres. Sans plafond,
 * un onglet trafique — ou simplement un enfant qui gribouille dix minutes —
 * poserait en base un objet de plusieurs mega-octets que tout le monde
 * telechargerait a chaque visite du musee. Les bornes sont donc dans ce
 * fichier, appelees des DEUX cotes, et `validerDessin` ne fait jamais confiance:
 * il RECONSTRUIT champ par champ, comme `validerVehicule`.
 */

/** Cote de la grille de dessin. Un dessin est un carre de 0 a 1000. */
export const COTE_DESSIN = 1000

/** Nombre maximal de traits dans un dessin. */
export const MAX_TRAITS = 160

/** Nombre maximal de points dans un seul trait. */
export const MAX_POINTS_TRAIT = 600

/** Nombre maximal de points, tous traits confondus. */
export const MAX_POINTS_DESSIN = 4000

/** Longueur maximale du titre, en caracteres. */
export const MAX_TITRE = 40

/** Epaisseurs proposees, en millemes du cote. */
export const EPAISSEURS: readonly number[] = [6, 14, 30, 60]

/**
 * La palette du musee.
 *
 * Douze couleurs et pas de selecteur libre: sur un telephone, un `input` de
 * type `color` ouvre la palette SYSTEME, qui recouvre le jeu et n'a rien a voir
 * avec lui. Douze pastilles au doigt suffisent, et c'est le meme choix que
 * l'ecole — pas de champ de saisie, un pave de boutons.
 */
export const PALETTE: readonly number[] = [
  0x1a1a1a, 0xffffff, 0xe03c3c, 0xf58b32, 0xf5d13a, 0x6fbf4a, 0x2f9e8f, 0x3f7fd4, 0x7b57c9, 0xd45fa8,
  0x9a6b3f, 0xb8bec4,
]

/** Un trait: une couleur, une epaisseur, et le chemin du doigt. */
export interface Trait {
  couleur: number
  /** Epaisseur en millemes du cote de la grille. */
  epaisseur: number
  /**
   * Points a plat: x0, z0, x1, z1, ... en entiers de 0 a COTE_DESSIN.
   *
   * A plat plutot qu'en objets {x, y}: un trait de six cents points fait ainsi
   * un tableau de mille deux cents nombres au lieu de six cents objets, ce qui
   * divise par cinq la taille du JSON et supprime autant d'allocations au
   * moment de le relire.
   */
  points: number[]
}

/** Un dessin accroche au musee. */
export interface Dessin {
  id: string
  /** Nom de COMPTE de l'auteur: c'est lui qui porte la couleur. */
  auteur: string
  /** Titre donne par l'auteur, accentue, deja nettoye. */
  titre: string
  traits: Trait[]
  /** Temps de JEU absolu de l'accrochage, sur l'horloge du monde. */
  creeA: number
  /**
   * Vrai si c'est CELUI QUI DEMANDE qui l'a accroche.
   *
   * Rempli par le serveur, qui seul connait les jetons; absent d'une base ou
   * d'un fichier, ou la question n'a pas de sens. C'est ce qui permet a un
   * invite — signe « invité » comme tous les invites — de retirer sa propre
   * toile et pas celle du voisin.
   */
  aMoi?: boolean
}

// ------------------------------------------------------------------ bornes

function entier(v: unknown, min: number, max: number): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return Math.max(min, Math.min(max, Math.round(v)))
}

/**
 * Nettoie un titre: espaces reduits, caracteres de controle retires, longueur
 * bornee.
 *
 * Les memes retraits que le chat, et pour la meme raison: un `\n` casserait en
 * deux une ligne du journal du serveur, et une marque de direction retournerait
 * l'affichage de tout ce qui suit dans la galerie.
 */
export function nettoyerTitre(brut: unknown): string {
  if (typeof brut !== 'string') return ''
  // La MEME liste que le chat (`CARACTERES_INVISIBLES` dans ./reseau.ts), et
  // pour la meme raison: un retour a la ligne casserait en deux une ligne du
  // journal, une marque de direction retournerait l'affichage de la galerie.
  const sansControle = brut.replace(
    /[\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g,
    ' ',
  )
  return sansControle.replace(/\s+/g, ' ').trim().slice(0, MAX_TITRE)
}

/**
 * Relit un dessin venu d'un JSON (base, message reseau, corps de requete).
 *
 * Rend `null` au lieu de jeter: une ligne abimee doit faire disparaitre UN
 * dessin, jamais empecher le musee de s'ouvrir. C'est la meme regle que pour la
 * sauvegarde du monde, et elle a deja servi — un seul batiment corrompu a rendu
 * le demarrage impossible.
 *
 * Un trait devenu vide (points impairs, hors bornes) est ECARTE, pas corrige:
 * decaler les coordonnees restantes dessinerait n'importe quoi, ce qui est pire
 * qu'un trait manquant.
 */
export function validerDessin(v: unknown): Dessin | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const id = typeof o['id'] === 'string' ? o['id'].slice(0, 64) : ''
  if (id.length === 0) return null
  const auteur = typeof o['auteur'] === 'string' ? o['auteur'].slice(0, 32) : ''
  const brut = o['traits']
  if (!Array.isArray(brut)) return null

  const traits: Trait[] = []
  let total = 0
  for (const t of brut) {
    if (traits.length >= MAX_TRAITS) break
    if (typeof t !== 'object' || t === null) continue
    const ot = t as Record<string, unknown>
    const couleur = entier(ot['couleur'], 0, 0xffffff)
    const epaisseur = entier(ot['epaisseur'], 1, 200)
    const pts = ot['points']
    if (couleur === null || epaisseur === null || !Array.isArray(pts)) continue
    // Un point, c'est DEUX nombres: une longueur impaire veut dire que la
    // trame a ete coupee, et on ne devine pas la moitie manquante.
    if (pts.length < 2 || pts.length % 2 !== 0) continue

    const points: number[] = []
    for (const n of pts) {
      if (points.length >= MAX_POINTS_TRAIT * 2 || total + points.length / 2 >= MAX_POINTS_DESSIN) break
      const c = entier(n, 0, COTE_DESSIN)
      if (c === null) {
        points.length = 0
        break
      }
      points.push(c)
    }
    // Le plafond peut avoir coupe au milieu d'un point: on retire l'orphelin.
    if (points.length % 2 !== 0) points.pop()
    if (points.length < 2) continue
    total += points.length / 2
    traits.push({ couleur, epaisseur, points })
  }

  if (traits.length === 0) return null
  const creeA = entier(o['creeA'], 0, Number.MAX_SAFE_INTEGER) ?? 0
  return { id, auteur, titre: nettoyerTitre(o['titre']), traits, creeA, aMoi: o['aMoi'] === true }
}

/**
 * Points gardes par un dessin de VIGNETTE.
 *
 * Une vignette de galerie fait 220 pixels de cote: elle ne peut pas montrer
 * quatre mille points, et les envoyer revient a payer cent fois le detail qu'on
 * affiche. Le budget est passe de 150 a 260 le jour ou l'allegement a cesse de
 * decimer pour SIMPLIFIER (voir `alleger`): 260 points choisis la ou le trait
 * TOURNE valent bien plus que 150 pris un sur N, et ca reste quinze fois moins
 * que le dessin entier (31 Kio au plafond).
 */
export const POINTS_VIGNETTE = 260

/**
 * Distance d'un point au segment [a, b]. Sert a `simplifier`.
 *
 * Un segment de longueur nulle (deux points identiques) se ramene a la distance
 * au point: sans ce cas, la division rendrait NaN et le trait entier
 * disparaitrait.
 */
function distanceAuSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const dx = bx - ax
  const dz = bz - az
  const len2 = dx * dx + dz * dz
  if (len2 === 0) return Math.hypot(px - ax, pz - az)
  let t = ((px - ax) * dx + (pz - az) * dz) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), pz - (az + t * dz))
}

function comptePoints(traits: readonly Trait[]): number {
  let total = 0
  for (const t of traits) total += t.points.length / 2
  return total
}

/**
 * En dessous de ce poids, un point ne se verrait JAMAIS: un demi-milleme de la
 * grille, soit un dixieme de pixel sur une vignette de 220. On arrete d'y
 * descendre — ce qui borne le travail dans les zones griffonnees, ou un doigt
 * pose mille points dans un centimetre carre.
 */
const POIDS_NEGLIGEABLE = 0.5

/**
 * CE QUE CHAQUE POINT APPORTE AU TRACE, en millemes de la grille
 * (Ramer-Douglas-Peucker, vu comme un CLASSEMENT plutot que comme un filtre).
 *
 * On descend l'arbre de decoupage habituel — la corde, le point qui s'en ecarte
 * le plus, et on recommence de part et d'autre — mais sans tolerance a
 * l'entree: on NOTE pour chaque point la distance a laquelle il s'ecartait de
 * sa corde. C'est exactement « a quelle grossierete ce point disparaitrait-il ».
 * Les deux extremites valent l'infini: elles ne disparaissent jamais — sans
 * elles, un rond devient un croissant.
 *
 * Le poids d'un point est PLAFONNE par celui de son parent, sinon un detail
 * minuscule niche dans un grand virage pourrait survivre au virage lui-meme, et
 * le trace garderait le grain en perdant la forme.
 *
 * UN SEUL PARCOURS repond ensuite a n'importe quel budget ET a n'importe quelle
 * tolerance (`simplifier` n'est que ce classement, coupe a une hauteur): c'est
 * la meme regle lue de deux facons, et non deux ecritures du meme algorithme.
 *
 * Ecrit sans recursion (une pile explicite): un trait fait jusqu'a six cents
 * points, et ceci tourne aussi sur le SERVEUR.
 */
export function poidsRdp(points: readonly number[]): number[] {
  const n = points.length / 2
  const poids = new Array<number>(Math.max(0, n)).fill(0)
  if (n === 0) return poids
  poids[0] = Infinity
  poids[n - 1] = Infinity
  if (n <= 2) return poids

  const pile: [number, number, number][] = [[0, n - 1, Infinity]]
  while (pile.length > 0) {
    const seg = pile.pop()
    if (!seg) break
    const [debut, fin, plafond] = seg
    if (fin - debut < 2) continue
    const ax = points[debut * 2] ?? 0
    const az = points[debut * 2 + 1] ?? 0
    const bx = points[fin * 2] ?? 0
    const bz = points[fin * 2 + 1] ?? 0
    let pire = -1
    let pireD = POIDS_NEGLIGEABLE
    for (let i = debut + 1; i < fin; i++) {
      const d = distanceAuSegment(points[i * 2] ?? 0, points[i * 2 + 1] ?? 0, ax, az, bx, bz)
      if (d > pireD) {
        pireD = d
        pire = i
      }
    }
    // Tout ce segment tient dans un dixieme de pixel: ses points restent a
    // zero, et on ne descend pas plus bas.
    if (pire < 0) continue
    const p = Math.min(pireD, plafond)
    poids[pire] = p
    pile.push([debut, pire, p], [pire, fin, p])
  }
  return poids
}

/**
 * SIMPLIFIE un trait: on garde ce qui s'ecarte de la corde d'au moins
 * `tolerance`, en millemes de la grille. Les deux extremites restent toujours.
 *
 * C'est `poidsRdp` coupe a une hauteur, et rien d'autre — une seule ecriture de
 * l'algorithme, deux facons de s'en servir. Une tolerance nulle ou negative ne
 * retire rien.
 */
export function simplifier(points: readonly number[], tolerance: number): number[] {
  const n = points.length / 2
  if (n <= 2 || tolerance <= 0) return [...points]
  const poids = poidsRdp(points)
  const sortie: number[] = []
  for (let i = 0; i < n; i++) {
    if ((poids[i] ?? 0) >= tolerance) sortie.push(points[i * 2] ?? 0, points[i * 2 + 1] ?? 0)
  }
  return sortie
}

/**
 * ALLEGE un dessin pour la galerie, en gardant sa FORME — et ses COURBES.
 *
 * POURQUOI CETTE FONCTION EXISTE. `/api/dessins` renvoyait chaque dessin en
 * entier: au plafond, 31 Kio piece, soit 3,6 Mio pour les 120 que le musee
 * garde — telecharges A CHAQUE ouverture de la galerie, sur un telephone, pour
 * afficher des vignettes de 220 pixels. Mesure faite, pas estimee.
 *
 * POURQUOI ELLE NE DECIME PLUS. Elle gardait un point sur N. Or un point sur N
 * ignore le DESSIN: sur une courbe, il jette precisement les points qui la font
 * tourner et garde ceux du milieu des lignes droites, si bien qu'un rond
 * revenait en polygone et qu'une boucle tracee au doigt revenait en zigzag. Le
 * proprietaire l'a dit dans ces termes: « on dessine des lignes courbes, on
 * revient a la galerie, elles sont droites ». C'etait litteralement vrai.
 *
 * ON GARDE DONC LES POINTS QUI PESENT (`poidsRdp`): chaque point sait de
 * combien il ecarte le trace de la corde qui le remplacerait, on trie, et on
 * garde les plus lourds jusqu'au budget. Le trait garde ses virages et perd ses
 * alignements. Les extremites pesent l'infini, donc elles restent — sans elles
 * un rond devient un croissant.
 *
 * LE BUDGET EST GLOBAL, ET C'EST VOULU: un dessin fait d'un grand aplat et d'un
 * petit visage donne ses points au visage, parce que ce sont les siens qui
 * pesent. Une tolerance unique par trait ne saurait pas faire ce partage.
 *
 * UN SEUL PARCOURS, quel que soit le depassement — mesure sur le pire cas du
 * format (des dessins de 4000 points, le plafond, tout en spirales): deux
 * millisecondes par dessin, ce qui vaut d'etre garde en memoire cote serveur
 * (voir `vignettes` dans server/index.ts), un dessin accroche ne changeant
 * jamais. Un plancher demeure: deux points par trait, que rien ne descend, donc
 * un dessin de cent-soixante traits depasse forcement 260 points. C'est le
 * format qui le dit, pas un choix a faire ici.
 *
 * Pure, donc appelable du serveur comme du navigateur; c'est le serveur qui
 * s'en sert, pour n'envoyer que ce qui sera affiche.
 */
export function alleger(traits: readonly Trait[], budget = POINTS_VIGNETTE): Trait[] {
  const copies = traits.map((t) => ({ ...t, points: [...t.points] }))
  if (comptePoints(copies) <= budget) return copies

  const poids = copies.map((t) => poidsRdp(t.points))
  // Le SEUIL: le poids du budget-ieme point le plus lourd, tous traits
  // confondus. Les extremites (poids infini) passent toujours devant.
  const tous: number[] = []
  for (const p of poids) tous.push(...p)
  tous.sort((a, b) => b - a)
  const seuil = tous[budget - 1] ?? -Infinity

  /*
   * LES EX AEQUO SONT COMPTES, pas laisses passer en bloc. Un trace regulier
   * (un cercle, une spirale) donne des dizaines de points au poids IDENTIQUE:
   * les garder tous parce qu'ils valent le seuil, c'est rendre presque deux
   * fois le budget demande — mesure faite sur le pire cas du format. On en
   * prend donc le nombre qui reste, dans l'ordre du trace.
   *
   * Les EXTREMITES (poids infini) ne sont jamais soumises a ce comptage: un
   * trait doit garder ses deux bouts, c'est le plancher du format, et un trait
   * ampute finirait ecarte par `validerDessin`.
   */
  let restePourLesEgaux = budget
  for (const p of poids) {
    for (const v of p) if (v > seuil) restePourLesEgaux--
  }

  const sortie: Trait[] = []
  for (const [i, t] of copies.entries()) {
    const p = poids[i] ?? []
    const points: number[] = []
    for (let k = 0; k < t.points.length / 2; k++) {
      const v = p[k] ?? 0
      let garde = v === Infinity || v > seuil
      if (!garde && v === seuil && restePourLesEgaux > 0) {
        restePourLesEgaux--
        garde = true
      }
      if (garde) points.push(t.points[k * 2] ?? 0, t.points[k * 2 + 1] ?? 0)
    }
    sortie.push({ ...t, points })
  }
  return sortie
}

/** Nombre de points d'un dessin: sert aux journaux et aux garde-fous. */
export function nombrePoints(d: Dessin): number {
  let n = 0
  for (const t of d.traits) n += t.points.length / 2
  return n
}

/**
 * Vrai si ce dessin a de quoi etre accroche.
 *
 * Un trait unique de deux points est un point d'encre, pas un dessin: on refuse
 * pour que le musee ne se remplisse pas de taches, et on le dit au joueur.
 */
export function dessinAccrochable(d: Dessin): boolean {
  return nombrePoints(d) >= 8
}

/**
 * Ajoute un point a un trait, en ECARTANT ce qui ne se verrait pas.
 *
 * Un doigt pose sur un ecran emet des dizaines d'evenements par seconde, dont
 * la plupart a moins d'un pixel du precedent: sans ce filtre, un trait de trois
 * centimetres consommait deux cents points sur les six cents autorises, et le
 * dessin butait sur son plafond avant d'etre commence.
 *
 * Le SEUIL est exprime sur la grille, pas en pixels: il vaut donc la meme chose
 * sur un telephone et sur un ecran de bureau, ce qui est tout l'interet de
 * dessiner en coordonnees normalisees.
 */
export const SEUIL_POINT = 4

export function ajouterPoint(t: Trait, x: number, z: number): boolean {
  const cx = Math.max(0, Math.min(COTE_DESSIN, Math.round(x)))
  const cz = Math.max(0, Math.min(COTE_DESSIN, Math.round(z)))
  const n = t.points.length
  if (n >= MAX_POINTS_TRAIT * 2) return false
  if (n >= 2) {
    const px = t.points[n - 2] ?? 0
    const pz = t.points[n - 1] ?? 0
    if (Math.abs(cx - px) < SEUIL_POINT && Math.abs(cz - pz) < SEUIL_POINT) return false
  }
  t.points.push(cx, cz)
  return true
}

/**
 * Identifiant d'un dessin: stable, sans horloge et sans hasard.
 *
 * Meme forme que `idBatiment` et `idVehicule` — un hachage de ce qui le decrit.
 * On y met le contenu ET l'auteur ET la date de jeu: deux dessins identiques
 * faits par la meme personne a deux moments differents sont deux dessins, et
 * doivent pouvoir etre accroches tous les deux.
 */
export function idDessin(d: Omit<Dessin, 'id'>): string {
  let h = 0x811c9dc5
  const melanger = (n: number): void => {
    h = Math.imul(h ^ (n >>> 0), 16777619) >>> 0
  }
  for (let i = 0; i < d.auteur.length; i++) melanger(d.auteur.charCodeAt(i))
  for (let i = 0; i < d.titre.length; i++) melanger(d.titre.charCodeAt(i))
  melanger(Math.round(d.creeA))
  for (const t of d.traits) {
    melanger(t.couleur)
    melanger(t.epaisseur)
    for (const p of t.points) melanger(p)
  }
  const h2 = Math.imul(h ^ 0x51ed270b, 2246822519) >>> 0
  return `d_${h.toString(36)}${h2.toString(36)}`
}
